import { RotateCcw, Share2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import ShareQuote from '@/features/Quote/components/modals/ShareQuote';
import { useHideHeader } from '@/hooks/useHideHeader';
import { isQuotesComplete } from '@/lib/quoteStatus';
import ContactButton from '../shared/ContactButton';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import ModalContact from '../shared/ModalContact';
import FlowGuidanceBar from './FlowGuidanceBar';
import ProgressIndicator from './ProgressIndicator';

const Header = (): React.JSX.Element => {
	const { t } = useTranslation();
	const { appData } = useAppContext();
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [shareModalOpen, setShareModalOpen] = useState(false);
	const hideHeader = useHideHeader();
	// The nav is `fixed` on mobile (so it stays put while the page scrolls), which
	// takes it out of document flow. Measuring its real height and reserving that
	// exact amount of space below it — rather than guessing a fixed padding value —
	// means every page gets the right offset automatically, whether or not it also
	// has a flow-guidance bar under the nav. A callback ref (rather than a plain
	// ref + effect) reacts to the nav actually mounting/unmounting, e.g. when
	// `hideHeader` toggles.
	const [navHeight, setNavHeight] = useState(0);
	const observerRef = useRef<ResizeObserver | null>(null);
	const navRef = useCallback((node: HTMLElement | null) => {
		observerRef.current?.disconnect();
		observerRef.current = null;
		if (!node) {
			setNavHeight(0);
			return;
		}
		const observer = new ResizeObserver(([entry]) => {
			// `getBoundingClientRect` (unlike `contentRect`) includes padding and
			// border, matching the nav's actual rendered footprint.
			if (entry) setNavHeight(entry.target.getBoundingClientRect().height);
		});
		observer.observe(node);
		observerRef.current = observer;
	}, []);
	const aanbodMatch = useMatch('/aanbod/:id');
	const aanvullingMatch = useMatch('/aanvulling/:id/:selected');
	const overzichtMatch = useMatch('/overzicht/:id/:selected');
	const aanvraagMatch = useMatch('/aanvraag/:id/:selected/:step?');

	const isQuotesLoaded = isQuotesComplete(appData?.status);
	const isOnAanbod = aanbodMatch != null;

	// The Share button must stay available throughout the quote flow (offer →
	// add-ons → overview → application), not just on the offer page. We pull the
	// id from whichever flow route is active. On /aanbod the quotes may still be
	// generating, so we wait until they're loaded; on the later flow routes a
	// quote has already been selected, so the offer always exists.
	const flowMatch = aanbodMatch ?? aanvullingMatch ?? overzichtMatch ?? aanvraagMatch;
	const policyUploadFormId = flowMatch?.params.id;
	const canShareQuote = policyUploadFormId != null && (!isOnAanbod || isQuotesLoaded);

	const handleContactClick = () => {
		setContactModalOpen(true);
	};

	return (
		<header>
			{!hideHeader && (
				<nav
					ref={navRef}
					className="flex items-center border-b border-border bg-background py-2 fixed md:relative top-0 left-0 right-0 z-50"
				>
					<div className="w-full xl:container xl:mx-auto px-5 flex items-center">
						{/* Logo - uses CSS custom property --logo-url from tenant theme */}
						<div
							role="img"
							aria-label="Logo"
							className="self-stretch bg-left bg-no-repeat bg-contain h-auto w-[180px] shrink-0 my-1 min-h-[40px]"
							style={{ backgroundImage: 'var(--logo-url)' }}
							data-testid="header-logo"
						/>
						<div className="ml-auto flex items-center gap-2 py-2 ps-3">
							{canShareQuote && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setShareModalOpen(true)}
									iconRight={<Share2 className="w-4 h-4" />}
									data-testid="share-quote-button"
									aria-label={t('header.shareQuote')}
								>
									{/* Label hidden below sm — the row is tight on phones, so the
									    action buttons collapse to their icons there. */}
									<span className="hidden sm:inline">{t('header.shareQuote')}</span>
								</Button>
							)}
							{isOnAanbod && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => {
										window.location.href = '/';
									}}
									iconRight={
										<RotateCcw className="w-4 h-4 group-hover:duration-500 group-hover:ease-in-out group-hover:rotate-[-360deg]" />
									}
									className="group"
									data-testid="header-button-begin-opnieuw"
									aria-label={t('header.startOver')}
								>
									<span className="hidden sm:inline">{t('header.startOver')}</span>
								</Button>
							)}
							<ContactButton onClick={handleContactClick} />
							<LanguageSwitcher />
						</div>
					</div>
				</nav>
			)}
			{/* Reserves the exact space the fixed nav occupies above it (measured,
			    not guessed) so the flow-guidance bar and page content below are
			    never hidden behind it — on any route, regardless of what else
			    the header renders. Desktop nav is `relative` (in flow already),
			    so no spacer is needed there. */}
			{!hideHeader && <div className="md:hidden" style={{ height: navHeight }} />}
			<ProgressIndicator
				onContactClick={handleContactClick}
				onShareClick={canShareQuote ? () => setShareModalOpen(true) : undefined}
			/>
			<FlowGuidanceBar />
			<ModalContact open={contactModalOpen} onOpenChange={setContactModalOpen} />
			{canShareQuote && (
				<ShareQuote
					open={shareModalOpen}
					onOpenChange={setShareModalOpen}
					policyUploadFormId={policyUploadFormId}
				/>
			)}
		</header>
	);
};

export default Header;
